import { twMerge } from 'tailwind-merge'
import { RiArrowDownSLine, RiArrowUpSLine, RiCodeLine } from 'react-icons/ri'
import { StickyEdgeShadow } from '../sticky-edge-shadow'
import { GTableColumn, GTableHeaderProps } from '../types'

export const TableHeader = <T,>(props: GTableHeaderProps<T>) => {
	const {
		column,
		reactColumn,
		sort,
		onSort,
		isScrolling,
		pinShadow = { left: false, right: false },
		variant
	} = props
	const isDs = variant === 'ds'

	const raiseSort = (column: GTableColumn<T>) => {
		if (onSort && column.sortKey) {
			if (column.sortKey === sort) return onSort(`-${column.sortKey}`)
			onSort(column.sortKey)
		}
	}
	const renderSortIcon = (column: GTableColumn<T>) => {
		if (`-${column.sortKey}` !== sort && column.sortKey !== sort)
			return <RiCodeLine className="h-3 w-3 rotate-90" />
		if (sort === column.sortKey) return <RiArrowUpSLine className="h-3 w-3" />
		if (sort === `-${column.sortKey}`)
			return <RiArrowDownSLine className="h-3 w-3" />
		return <RiCodeLine className="h-3 w-3 rotate-90" />
	}

	if (!column) return null

	const { key, ...headerProps } = reactColumn.getHeaderProps()
	// Fixed table layout sizes every column from the HEADER row, so a width
	// declared only on the body cell is not the width that renders — and every
	// sticky offset is derived from a rendered width. Scoped to the `ds` variant:
	// the admin grids also carry `width`, have never had it honoured here, and
	// pin nothing that would depend on it.
	const widthStyle =
		isDs && column.width && !column.stretch
			? {
					width: column.width,
					minWidth: column.width,
					maxWidth: column.width
			  }
			: {}
	const pinStyle = {
		...(column.freeze ? { left: column.freezeLeftOffset ?? 0 } : {}),
		...(column.freezeRight ? { right: column.freezeRightOffset ?? 0 } : {})
	}

	return (
		<th
			key={key}
			{...headerProps}
			style={{ ...(headerProps.style ?? {}), ...widthStyle, ...pinStyle }}
			className={twMerge(
				isDs
					? 'px-6 py-3 h-[44px] text-right font-grotesk text-[12px] leading-4 font-semibold text-t-desc-gray border-b border-b-t-checkbox bg-table-band'
					: 'px-4 py-3 text-right text-sm font-medium text-t-desc-gray border-b border-b-t-checkbox',
				column.hidden ? 'hidden' : '',
				column.mobileHide ? 'hidden md:table-cell' : '',
				column.tabletHide ? 'hidden lg:table-cell' : '',
				column.parentClassName || '',
				column.freeze || column.freezeRight ? 'sticky z-20' : '',
				isScrolling ? (isDs ? 'bg-table-band' : 'bg-t-smoke') : ''
			)}
		>
			{column.freezeEdge && (
				<StickyEdgeShadow
					side={column.freezeEdge}
					show={
						column.freezeEdge === 'right' ? pinShadow.left : pinShadow.right
					}
				/>
			)}
			<div
				className={twMerge(
					isDs
						? 'flex w-full h-full justify-end items-center gap-1.5'
						: 'flex w-full text-sm h-full px-2 justify-end items-center',
					column.className || ''
				)}
			>
				{column.header
					? column.header()
					: typeof column.label === 'function'
					? column.label()
					: column.label}
				{column.sortKey && (
					<span
						className={twMerge('cursor-pointer', isDs ? '' : 'mx-2')}
						onClick={() => raiseSort(column)}
					>
						{renderSortIcon(column)}
					</span>
				)}
			</div>
		</th>
	)
}
